<template>
  <v-card flat>
    <v-card-text>
      <!-- ================================================================= -->
      <!-- SECTION 1: INFO MESSAGE                                         -->
      <!-- ================================================================= -->
      <h3 class="headline">Infonachricht bearbeiten</h3>
      <p>Die Nachricht wird auf der Startseite (Fahrzeugauswahl) als Infobanner angezeigt.</p>
      <v-row align="center">
        <v-col cols="12" md="9">
          <v-text-field v-model="messageText" label="Infotext" outlined hide-details></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-btn color="default" dark :loading="isSavingMessage" @click="saveMessage()">
            <v-icon left>mdi-content-save</v-icon> Speichern
          </v-btn>
        </v-col>
      </v-row>

      <v-divider class="my-6"></v-divider>

      <!-- ================================================================= -->
      <!-- SECTION 2: FILE MANAGEMENT                                      -->
      <!-- ================================================================= -->
      <div class="d-flex justify-space-between align-center mb-4">
        <h3 class="headline">Dateien verwalten</h3>
        <v-btn color="default" dark @click="openUploadDialog()">
          <v-icon left>mdi-upload</v-icon>
          Datei hochladen
        </v-btn>
      </div>
      <p>Die Dateien können von allen Benutzern auf der Startseite heruntergeladen werden.</p>
      <v-data-table
          :headers="fileHeaders"
          :items="fileList"
          :loading="loadingFiles"
          class="elevation-1"
      >
        <template v-slot:item.actions="{ item }">
          <v-icon small color="red" title="Datei löschen" @click="confirmDeleteFile(item)">mdi-delete</v-icon>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- ================================================================= -->
    <!-- DIALOGS                                                         -->
    <!-- ================================================================= -->
    <!-- Dialog 1: File Upload -->
    <v-dialog v-model="dialog.upload" persistent max-width="600px">
      <v-card>
        <v-card-title><span class="headline">Neue Datei hochladen</span></v-card-title>
        <v-card-text>
          <v-text-field v-model="fileTitle" label="Titel der Datei" required outlined class="mb-4"></v-text-field>
          <v-file-input v-model="fileToUpload" label="Datei auswählen" required outlined prepend-icon="mdi-paperclip"></v-file-input>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeUploadDialog">Abbrechen</v-btn>
          <v-btn color="primary" text :loading="isSavingFile" @click="uploadFile">Hochladen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog 2: Delete File Confirmation -->
    <v-dialog v-model="dialog.delete" max-width="450px">
      <v-card v-if="fileToDelete">
        <v-card-title class="headline">Datei "{{ fileToDelete.title }}" löschen?</v-card-title>
        <v-card-text>
          Sind Sie sicher, dass Sie diese Datei permanent vom Server löschen möchten?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog.delete = false">Abbrechen</v-btn>
          <v-btn color="red darken-1" text :loading="isSavingFile" @click="deleteFile">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.state" :timeout="snack.timeout" :color="snack.color" top right>{{ snack.message }}</v-snackbar>
  </v-card>
</template>

<script>
import axios from 'axios';

export default {
  name: "AppSettings",
  data() {
    return {
      // Message State
      messageText: '',
      isSavingMessage: false,

      // File State
      fileList: [],
      loadingFiles: false,
      isSavingFile: false,
      dialog: { upload: false, delete: false },
      fileTitle: '',
      fileToUpload: null,
      fileToDelete: null,
      fileHeaders: [
        { text: 'Titel', align: 'start', value: 'title' },
        { text: 'Dateiname', value: 'path' },
        { text: 'Aktionen', value: 'actions', sortable: false, align: 'end' },
      ],

      snack: { state: false, color: 'success', timeout: 3000, message: '' }
    }
  },
  mounted() {
    this.fetchMessage();
    this.fetchFiles();
  },
  methods: {
    // --- Message Methods ---
    fetchMessage() {
      axios.get('/messages')
          .then(response => { this.messageText = response.data ? response.data.text : ''; })
          .catch(() => this.showSnackbar("Infonachricht konnte nicht geladen werden.", "error"));
    },
    saveMessage() {
      this.isSavingMessage = true;
      axios.post('/messages', { 'text': this.messageText })
          .then(() => this.showSnackbar("Nachricht wurde gespeichert."))
          .catch(() => this.showSnackbar("Fehler beim Speichern.", "error"))
          .finally(() => this.isSavingMessage = false);
    },

    // --- File Methods ---
    fetchFiles() {
      this.loadingFiles = true;
      axios.get('/files')
          .then(response => { this.fileList = response.data || []; })
          .catch(() => this.showSnackbar("Dateiliste konnte nicht geladen werden.", "error"))
          .finally(() => this.loadingFiles = false);
    },
    openUploadDialog() {
      this.fileTitle = '';
      this.fileToUpload = null;
      this.dialog.upload = true;
    },
    closeUploadDialog() {
      this.dialog.upload = false;
      this.fileTitle = '';
      this.fileToUpload = null;
    },
    uploadFile() {
      if (!this.fileTitle || !this.fileToUpload) {
        this.showSnackbar("Bitte Titel und Datei angeben.", "warning");
        return;
      }
      this.isSavingFile = true;
      let formData = new FormData();
      formData.append("attachment", this.fileToUpload);
      formData.append("title", this.fileTitle);

      axios.post('/files', formData)
          .then(() => {
            this.showSnackbar("Datei wurde erfolgreich hochgeladen.");
            this.closeUploadDialog();
            this.fetchFiles();
          })
          .catch(() => this.showSnackbar("Fehler beim Upload.", "error"))
          .finally(() => this.isSavingFile = false);
    },
    confirmDeleteFile(file) {
      this.fileToDelete = file;
      this.dialog.delete = true;
    },
    deleteFile() {
      this.isSavingFile = true;
      axios.delete(`/files/${this.fileToDelete._id}`)
          .then(() => {
            this.showSnackbar("Datei wurde entfernt.");
            this.dialog.delete = false;
            this.fetchFiles();
          })
          .catch(() => this.showSnackbar("Fehler beim Löschen.", "error"))
          .finally(() => this.isSavingFile = false);
    },

    showSnackbar(message, color = 'success') {
      this.snack.message = message;
      this.snack.color = color;
      this.snack.state = true;
    }
  }
}
</script>